/**
 * Icon design tokens inspired by Apple's SF Symbols and Nothing Technology's geometric precision
 * Provides consistent stroke weights, corner radii, and sizing for iconography
 */

export const iconTokens = {
  // Icon sizes following Apple's sizing guidelines
  size: {
    xs: 12,
    sm: 16,
    md: 20,
    lg: 24,
    xl: 28,
    '2xl': 32,
    '3xl': 40,
    '4xl': 48,
    base: 20, // Add base size
  },

  // Stroke weights for consistent line thickness
  strokeWidth: {
    thin: 1,
    regular: 1.5,
    medium: 2,
    semibold: 2.5,
    bold: 3,
  },

  // Corner radius for rounded icons (Nothing-inspired geometric precision)
  cornerRadius: {
    none: 0,
    sm: 2,
    md: 4,
    lg: 6,
    xl: 8,
    full: 9999,
  },

  // Icon variants for different contexts
  variants: {
    // Standard outline icons
    outline: {
      strokeWidth: 'regular' as const,
      fill: 'none',
    },
    // Filled solid icons
    filled: {
      strokeWidth: 'none' as const,
      fill: 'currentColor',
    },
    // Duotone icons with primary and secondary colors
    duotone: {
      strokeWidth: 'regular' as const,
      fill: 'currentColor',
      opacity: {
        primary: 1,
        secondary: 0.3,
      },
    },
    // Nothing-inspired minimal icons
    minimal: {
      strokeWidth: 'thin' as const,
      fill: 'none',
    },
  },

  // Touch target sizes for icon buttons
  touchTarget: {
    sm: 32,
    md: 44, // Apple's minimum touch target
    lg: 48,
    xl: 56,
  },
} as const;

// Common icon names used throughout the app
export const iconNames = {
  // Navigation
  home: 'home',
  search: 'search',
  profile: 'profile',
  menu: 'menu',
  back: 'back',
  close: 'close',
  x: 'x',
  
  // Actions
  heart: 'heart',
  bookmark: 'bookmark',
  share: 'share',
  edit: 'edit',
  delete: 'delete',
  add: 'add',
  
  // Status
  check: 'check',
  warning: 'warning',
  error: 'error',
  info: 'info',
  loading: 'loading',
  
  // Coffee-specific
  coffee: 'coffee',
  timer: 'timer',
  temperature: 'temperature',
  grinder: 'grinder',
  scale: 'scale',
  
  // Media
  play: 'play',
  pause: 'pause',
  camera: 'camera',
  image: 'image',
  video: 'video',
  
  // Interface
  chevronUp: 'chevron-up',
  chevronDown: 'chevron-down',
  chevronLeft: 'chevron-left',
  chevronRight: 'chevron-right',
  dots: 'dots',
  filter: 'filter',
  sort: 'sort',
  
  // Theme and settings
  sun: 'sun',
  moon: 'moon',
  settings: 'settings',
  
  // Seasonal
  snowflake: 'snowflake',
  flower: 'flower',
  leaf: 'leaf',
  
  // Custom themes
  circle: 'circle',
  fire: 'fire',
  droplet: 'droplet',
  
  // Additional icons
  eye: 'eye',
} as const;

// Type definitions
export type IconSize = keyof typeof iconTokens.size;
export type IconStrokeWidth = keyof typeof iconTokens.strokeWidth;
export type IconCornerRadius = keyof typeof iconTokens.cornerRadius;
export type IconVariant = keyof typeof iconTokens.variants;
export type IconTouchTarget = keyof typeof iconTokens.touchTarget;
export type IconName = keyof typeof iconNames;

// Icon configuration interface
export interface IconConfig {
  size?: IconSize;
  strokeWidth?: IconStrokeWidth;
  variant?: IconVariant;
  color?: string;
}

// Nothing-inspired geometric patterns
export const geometricPatterns = {
  // Dot matrix patterns
  dotMatrix: {
    small: {
      size: 2,
      spacing: 8,
      opacity: 0.1,
    },
    medium: {
      size: 3,
      spacing: 12,
      opacity: 0.15,
    },
    large: {
      size: 4,
      spacing: 16,
      opacity: 0.2,
    },
  },
  
  // Grid patterns
  grid: {
    fine: {
      strokeWidth: 0.5,
      spacing: 8,
      opacity: 0.05,
    },
    medium: {
      strokeWidth: 1,
      spacing: 16,
      opacity: 0.1,
    },
    coarse: {
      strokeWidth: 1.5,
      spacing: 24,
      opacity: 0.15,
    },
  },
  
  // Radial patterns
  radial: {
    subtle: {
      rings: 3,
      spacing: 20,
      strokeWidth: 0.5,
      opacity: 0.05,
    },
    prominent: {
      rings: 5,
      spacing: 30,
      strokeWidth: 1,
      opacity: 0.1,
    },
  },
} as const;

export type DotMatrixSize = keyof typeof geometricPatterns.dotMatrix;
export type GridSize = keyof typeof geometricPatterns.grid;
export type RadialSize = keyof typeof geometricPatterns.radial;